// Angular
import { Injectable } from '@angular/core';

// Libraries
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { TranslateService } from '@ngx-translate/core';
import { takeUntil } from 'rxjs';
import { append, insertItem, patch, removeItem, updateItem } from '@ngxs/store/operators';

// Services
import { CoreService } from '../../../shared/services';
import { JobService } from '../services/job.service';
import { SpinnerService } from '../../../shared/components/spinner/services/spinner.service';

// Models
import { CompletedJobModel } from '../models/completed-job-model';
import { AppCleanup } from '../../../shared/models';
import { Job } from '../models';

// Enums
import { StatusType } from '../../../shared/enums';

// Store
import { JobActions } from './job.action';

export interface JobStateModel {
	cachedJobsLoaded: boolean;
	cachedJobs: Job[];
	activeJobsLoaded: boolean;
	activeJobs: Job[];
	filteredActiveJobs: Job[];
	completedJobs: CompletedJobModel[];
	completedJobsLoaded: boolean;
}

const jobDefaultState: JobStateModel = {
	cachedJobsLoaded: false,
	cachedJobs: undefined,
	activeJobsLoaded: false,
	activeJobs: undefined,
	filteredActiveJobs: undefined,
	completedJobs: undefined,
	completedJobsLoaded: false,
};

@State<any>({
	name: 'job',
	defaults: jobDefaultState,
})
@Injectable()
export class JobState extends AppCleanup {
	constructor(
		private jobService: JobService,
		private coreService: CoreService,
		private translateService: TranslateService,
		private spinnerService: SpinnerService
	) {
		super();
	}

	@Action(JobActions.SetAllCachedJobs)
	setAllCachedJobs(ctx: StateContext<JobStateModel>, action: JobActions.SetAllCachedJobs) {
		ctx.patchState({
			cachedJobs: action.jobs,
			cachedJobsLoaded: true,
		});
	}

	@Action(JobActions.AddCachedJob)
	addCachedJob(ctx: StateContext<JobStateModel>, action: JobActions.AddCachedJob) {
		const currentState = ctx.getState();
		const existingCachedJob = currentState.cachedJobs?.find((item) => item.id === action.job.id);
		if (existingCachedJob) {
			ctx.dispatch(new JobActions.UpdateCachedJob(action.job));
		} else {
			ctx.setState(
				patch({
					cachedJobs: append([action.job]),
					cachedJobsLoaded: true,
				})
			);
		}
	}

	@Action(JobActions.UpdateCachedJob)
	updateCachedJob(ctx: StateContext<JobStateModel>, action: JobActions.UpdateCachedJob) {
		const currentState = ctx.getState();

		const existingCachedJob = currentState.cachedJobs?.find((item) => item.id === action.job.id);

		if (existingCachedJob) {
			ctx.setState(
				patch({
					cachedJobs: updateItem((j) => j.id === action.job.id, action.job),
					cachedJobsLoaded: true,
				})
			);
		}
	}

	@Action(JobActions.GetAllActiveJobs)
	getAllActiveJobs(ctx: StateContext<JobStateModel>, action: JobActions.GetAllActiveJobs) {
		if (action.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.jobService
			.getActiveJobs()
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (activeJobs) => {
					ctx.patchState({
						activeJobs: activeJobs,
						activeJobsLoaded: true,
					});

					const state = ctx.getState();
					if (state.cachedJobs && state.cachedJobs?.length > 0) {
						activeJobs.forEach((activeJob) => {
							let found: boolean = false;
							state.cachedJobs.forEach((cachedJob) => {
								if (activeJob.id === cachedJob.id) {
									found = true;
								}
							});

							if (!found) {
								ctx.dispatch(new JobActions.AddCachedJob(activeJob));
							}
						});
					} else {
						ctx.dispatch(new JobActions.SetAllCachedJobs(activeJobs));
					}

					if (action.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}
				},
				error: () => {
					if (action.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.showToast) {
						const message = `${this.translateService.instant('GENERIC.ERROR_GETTING', { name: this.translateService.instant('MODULE.JOB.ACTIVE_JOB.TITLE_PLURAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Action(JobActions.SetAllActiveJobs)
	setAllActiveJobs(ctx: StateContext<JobStateModel>, action: JobActions.SetAllActiveJobs) {
		ctx.patchState({
			activeJobs: action.jobs,
			activeJobsLoaded: true,
		});
	}

	@Action(JobActions.AddActiveJob)
	addActiveJob(ctx: StateContext<JobStateModel>, action: JobActions.AddActiveJob) {
		const currentState = ctx.getState();
		const existingActiveJob = currentState.activeJobs?.find((item) => item.id === action.job.id);
		if (existingActiveJob) {
			ctx.dispatch(new JobActions.UpdateActiveJob(action.job));
		} else {
			ctx.setState(
				patch({
					activeJobs: insertItem(action.job, 0),
					activeJobsLoaded: true,
				})
			);
		}
	}

	@Action(JobActions.UpdateActiveJob)
	updateActiveJob(ctx: StateContext<JobStateModel>, action: JobActions.UpdateActiveJob) {
		const currentState = ctx.getState();
		const existingActiveJob = currentState.activeJobs?.find((item) => item.id === action.job.id);

		if (existingActiveJob) {
			ctx.setState(
				patch({
					activeJobs: updateItem((j) => j.id === action.job.id, action.job),
					activeJobsLoaded: true,
				})
			);
		}

		const existingCachedJob = currentState.cachedJobs?.find((item) => item.id === action.job.id);

		if (existingCachedJob) {
			ctx.setState(
				patch({
					cachedJobs: updateItem((j) => j.id === action.job.id, action.job),
					cachedJobsLoaded: true,
				})
			);
		}
	}

	@Action(JobActions.RemoveActiveJob)
	removeActiveJob(ctx: StateContext<JobStateModel>, action: JobActions.RemoveActiveJob) {
		const currentState = ctx.getState();
		const existingItem = currentState.activeJobs?.find((item) => item.id === action.job.id);

		if (existingItem) {
			ctx.setState(
				patch({
					activeJobs: removeItem((job) => job.id === action.job.id),
				})
			);
		}
	}

	@Action(JobActions.FilterActiveJobs)
	filterActiveJobs(ctx: StateContext<JobStateModel>, action: JobActions.FilterActiveJobs) {
		let activeJobs = ctx.getState().activeJobs;
		activeJobs.filter((job) => job.jobTypeId);
		let filteredActiveJobs: Job[];

		if (action.jobTypeIds.length > 0) {
			filteredActiveJobs = activeJobs.filter((job) => action.jobTypeIds.includes(job.jobTypeId));
		} else {
			filteredActiveJobs = activeJobs;
		}

		ctx.patchState({
			filteredActiveJobs: filteredActiveJobs,
		});
	}

	@Action(JobActions.GetAllCompletedJobs)
	getAllCompletedJobs(ctx: StateContext<JobStateModel>, action: JobActions.GetAllCompletedJobs) {
		return this.jobService
			.getCompletedJobs(action.data.searchModel)
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (completedJobs) => {
					ctx.patchState({
						completedJobs: completedJobs,
						completedJobsLoaded: true,
					});
				},
				error: () => {
					const message = `${this.translateService.instant('MODULE.JOB.COMPLETED_JOB_SEARCH_ERROR')}`;
					this.coreService.toast({ message: message, type: StatusType.DANGER });
				},
			});
	}

	@Action(JobActions.SetAllCompletedJobs)
	setAllCompletedJobs(ctx: StateContext<JobStateModel>, action: JobActions.SetAllCompletedJobs) {
		ctx.patchState({
			completedJobs: action.jobs,
			completedJobsLoaded: true,
		});
	}

	@Action(JobActions.AddCompletedJob)
	addCompletedJob(ctx: StateContext<JobStateModel>, action: JobActions.AddCompletedJob) {
		ctx.setState(
			patch({
				completedJobs: append([action.job]),
			})
		);
	}

	@Selector()
	static cachedJobsLoaded(state: JobStateModel): boolean {
		return state.cachedJobsLoaded;
	}

	@Selector()
	static cachedJobs(state: JobStateModel): Job[] {
		return state.cachedJobs;
	}

	@Selector()
	static activeJobsLoaded(state: JobStateModel): boolean {
		return state.activeJobsLoaded;
	}

	@Selector()
	static activeJobs(state: JobStateModel): Job[] {
		return state.activeJobs;
	}

	@Selector()
	static filteredActiveJobs(state: JobStateModel): Job[] {
		return state.filteredActiveJobs;
	}

	@Selector()
	static completedJobsLoaded(state: JobStateModel): boolean {
		return state.completedJobsLoaded;
	}

	@Selector()
	static completedJobs(state: JobStateModel): CompletedJobModel[] {
		return state.completedJobs;
	}
}
